import {track} from "@/head-monitoring/track";


function assetUrl(el: HTMLElement): string | undefined {
  return (
    (el as HTMLImageElement | HTMLScriptElement | HTMLSourceElement).src ||
    (el as HTMLLinkElement).href ||
    undefined
  );
}

type ResourceEntry = PerformanceResourceTiming & { responseStatus?: number };

const reportedAssetUrls = new Set<string>();
function reportedOnce(url: string | undefined): boolean {
  if (!url) return false;
  if (reportedAssetUrls.has(url)) return true;
  reportedAssetUrls.add(url);
  return false;
}

function isFailedResource(entry: ResourceEntry): boolean {
  const status = entry.responseStatus ?? 0;
  if (status >= 400) return true;
  return status === 0 && entry.transferSize === 0 && entry.encodedBodySize === 0;
}

function observeResourceFailures(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as ResourceEntry[]) {
        const t = entry.initiatorType;
        if (t !== 'fetch' && t !== 'xmlhttprequest' && t !== 'script') continue;
        if (!isFailedResource(entry)) continue;
        if (reportedOnce(entry.name)) continue;

        track('assetError', {
          content: {
            errorType: 'fetch_failed',
            errorPageUrl: location.pathname,
            errorSource: entry.name,
          },
        });
      }
    });
    obs.observe({ type: 'resource', buffered: true });
  } catch {
    // 'resource' entryType unsupported — silent no-op.
  }
}

export function initErrorTracking(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener(
    'error',
    (e) => {
      if (e.target instanceof HTMLElement) {
        const url = assetUrl(e.target);
        if (reportedOnce(url)) return;
        track('assetError', {
          content: {
            errorType: 'asset_load_error',
            errorPageUrl: location.pathname,
            errorSource: url,
          },
        });
        return;
      }

      const ee = e as ErrorEvent;
      track('assetError', {
        content: {
          errorType: 'js_error',
          errorPageUrl: location.pathname,
          errorMessage: ee.message,
          errorSource: ee.filename,
        },
      });
    },
    true,
  );

  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const isError = reason instanceof Error;
    track('assetError', {
      content: {
        errorType: 'unhandled_rejection',
        errorPageUrl: location.pathname,
        errorMessage: isError ? reason.message : String(reason),
      },
    });
  });

  observeResourceFailures();
}
